import { jsx, jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Flex, Grid, Select, Typography } from "antd";
import "./statsFilter.css";
import { ArrowDown, useTranslation } from "@inscore/shared";
import { ReactComponent as ArrowLeft } from "./../../../../assets/icons/arrow-left.svg";
import { ReactComponent as ArrowRight } from "./../../../../assets/icons/arrow-right.svg";
import { translateOr } from "../../statsService";
const { Text } = Typography;
const { useBreakpoint } = Grid;
const RANGE_FILTER_KIND = 2;
const getSelectedKey = (filter, filters) => {
  var _a;
  return String((_a = filters == null ? void 0 : filters[filter.key]) != null ? _a : filter.selectedKey);
};
const FilterButton = ({ active, onClick, children }) => /* @__PURE__ */ jsx(
  "button",
  {
    type: "button",
    className: `stats-filter-button ${active ? "active" : ""}`,
    onClick,
    children
  }
);
const ScrollableButtons = ({ activeKey, children }) => {
  const scrollRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    overflowing: false,
    atStart: true,
    atEnd: true
  });
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el)
      return;
    setScrollState({
      overflowing: el.scrollWidth > el.clientWidth + 1,
      atStart: el.scrollLeft <= 1,
      atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
    });
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el)
      return;
    updateScrollState();
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);
    el.addEventListener("scroll", updateScrollState, { passive: true });
    return () => {
      resizeObserver.disconnect();
      el.removeEventListener("scroll", updateScrollState);
    };
  }, [updateScrollState]);
  const isFirstScroll = useRef(true);
  useEffect(() => {
    const el = scrollRef.current;
    const active = el == null ? void 0 : el.querySelector(".stats-filter-button.active");
    if (!el || !active)
      return;
    const target = active.offsetLeft + active.offsetWidth / 2 - el.clientWidth / 2;
    const maxScroll = el.scrollWidth - el.clientWidth;
    el.scrollTo({
      left: Math.max(0, Math.min(target, maxScroll)),
      // jump on mount, animate on later selection changes
      behavior: isFirstScroll.current ? "auto" : "smooth"
    });
    isFirstScroll.current = false;
  }, [activeKey]);
  const scrollBy = (direction) => {
    const el = scrollRef.current;
    el == null ? void 0 : el.scrollBy({
      left: direction * el.clientWidth * 0.8,
      behavior: "smooth"
    });
  };
  return /* @__PURE__ */ jsxs(Flex, { align: "center", gap: 4, className: "stats-scrollable-buttons", children: [
    scrollState.overflowing && /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "stats-scroll-arrow",
        onClick: () => scrollBy(-1),
        disabled: scrollState.atStart,
        "aria-label": "Scroll left",
        children: /* @__PURE__ */ jsx(ArrowLeft, { className: "stroke-base" })
      }
    ),
    /* @__PURE__ */ jsx("div", { ref: scrollRef, className: "stats-scrollable-buttons-track", children }),
    scrollState.overflowing && /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "stats-scroll-arrow",
        onClick: () => scrollBy(1),
        disabled: scrollState.atEnd,
        "aria-label": "Scroll right",
        children: /* @__PURE__ */ jsx(ArrowRight, { className: "stroke-base" })
      }
    )
  ] });
};
const StatsFilter = ({
  filterOptions,
  filters,
  keyProperty,
  onFilterChange,
  token,
  renderNewVersion = true
}) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { t } = useTranslation();
  const buttonFilters = filterOptions.filter(
    (filter) => filter.kind !== RANGE_FILTER_KIND && filter.key !== "total"
  );
  if (renderNewVersion || isMobile) {
    if (!buttonFilters.length)
      return null;
    return /* @__PURE__ */ jsx(Flex, { vertical: true, gap: 8, className: "stats-filter-container", children: buttonFilters.map((filter) => {
      const selectedKey = getSelectedKey(filter, filters);
      return /* @__PURE__ */ jsx(
        "div",
        {
          className: "stats-filter-buttons",
          style: {
            gridTemplateColumns: `repeat(${filter.options.length}, 1fr)`
          },
          children: filter.options.map((option) => /* @__PURE__ */ jsx(
            FilterButton,
            {
              active: String(option.key) === selectedKey,
              onClick: () => onFilterChange(filter.key, option.key),
              children: translateOr(
                t,
                `stats_filter_${option.key}`,
                option.label
              )
            },
            option.key
          ))
        },
        filter.key
      );
    }) });
  }
  return /* @__PURE__ */ jsx(
    Flex,
    {
      flex: "1",
      align: "center",
      justify: "end",
      gap: 16,
      style: { flexWrap: "wrap" },
      className: "stats-filter-container",
      children: filterOptions.filter((filter) => filter.key !== "total").map((filter) => /* @__PURE__ */ jsxs(
        Flex,
        {
          align: "center",
          justify: "start",
          gap: 12,
          children: [
            /* @__PURE__ */ jsxs(
              Text,
              {
                style: {
                  color: token.colorTextSecondary,
                  fontSize: token.fontSizeSM
                },
                children: [
                  filter.label,
                  ":"
                ]
              }
            ),
            /* @__PURE__ */ jsx(
              Select,
              {
                className: "filter-select",
                popupMatchSelectWidth: false,
                placement: "bottomRight",
                defaultValue: filter.selectedKey,
                onChange: (value) => onFilterChange(filter.key, value),
                options: filter.options.map((option) => ({
                  label: option.label,
                  value: option.key
                })),
                suffixIcon: /* @__PURE__ */ jsx(
                  ArrowDown,
                  {
                    className: "stroke-base",
                    style: {
                      width: 18,
                      height: 18
                    }
                  }
                )
              }
            )
          ]
        },
        `${keyProperty}_${filter.key}`
      ))
    }
  );
};
const RANGE_FILTER_TITLES = {
  minuteRange: "choose_minute_range",
  total: "choose_total"
};
const StatsRangeFilter = memo(
  ({ filterOptions, filters, onFilterChange }) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { t } = useTranslation();
    const rangeFilters = filterOptions.filter(
      (filter) => filter.kind === RANGE_FILTER_KIND
    );
    if (!rangeFilters.length)
      return null;
    return /* @__PURE__ */ jsx(Flex, { vertical: true, gap: isMobile ? 12 : 16, children: rangeFilters.map((filter) => {
      const selectedKey = getSelectedKey(filter, filters);
      const titleKey = RANGE_FILTER_TITLES[filter.key];
      return /* @__PURE__ */ jsxs(
        Flex,
        {
          vertical: true,
          gap: 12,
          className: "stats-range-filter",
          children: [
            /* @__PURE__ */ jsxs(Text, { className: "stats-range-filter-title", children: [
              titleKey ? translateOr(t, titleKey, filter.label) : filter.label,
              ":"
            ] }),
            /* @__PURE__ */ jsx(ScrollableButtons, { activeKey: selectedKey, children: filter.options.map((option) => /* @__PURE__ */ jsx(
              FilterButton,
              {
                active: String(option.key) === selectedKey,
                onClick: () => onFilterChange(
                  filter.key,
                  option.key
                ),
                children: filter.key === "minuteRange" ? isMobile ? option.key : `${option.key} ${t("min_short")}` : option.label
              },
              option.key
            )) })
          ]
        },
        filter.key
      );
    }) });
  }
);
var stdin_default = memo(StatsFilter);
export {
  RANGE_FILTER_KIND,
  StatsRangeFilter,
  stdin_default as default
};
